/* eslint-disable no-param-reassign */
import _ from 'underscore';
import Promise from 'bluebird';
import Backbone from 'backbone';

import { FilterEntity } from '@bingads-webui/filter-entity';
import { FilterStorageType, getValidFilterEntity } from '@bingads-webui/filter-commons';
import { parseSync, stringifySync } from '@bingads-webui/safe-json';
import { FilterSchema, flattenEntitySchema, buildNameToPropertyMapping } from '@bingads-webui/filter-schema';
import { mergeFilterEntity } from './filter-service-util';

/**
 * Maintain FilterState<br>
 * Flatten entity schema<br>
 * Provides method to crud two types of filter which are<br>
 * QuickFilter(SelectorFilter)- Filter dropdown<br>
 * EditableFilter - Whose query can be edited by user<br>
 * Manages filter storages and creation of FilterEntity
 * Trigger filter-state-changed event and expose current combined filter in
 * the form of FilterEntity<br>
 * @extends external:"Backbone.Events"
 * @fires FilterService#filter-state-changed
 */

export class FilterService {
  /**
   * @param {Object} storageFactory - Factory method to create storage instance
   * @param {Object} storageOptions - Options for creating storage instance
   * @param {Object} [storageOptions.quickFilterOptions] - Options for creating quick
   * storage instance
   * @param {Object} [storageOptions.editableFilterOptions] - Options for creating
   *  editable storage instance
   * @param {Object} [storageOptions.savedFilterOptions] - Options for creating
   *  saved storage instance
   * @param {Object} entitySchema - Entity Schema
   * @param {Object} odata - Options for converting parameters to odata
   * @param {boolean|string[]} odata.ignoreCase - ignoreCase for odata convertion.
   * @param {Object} odata.custom - custom operator map for odata convertion.
   * @param {function} schemaProcessor - Update schema based on feature specific logic
   * @param {function} odataOptionsProcessor - Update odata options based on feature
   * specific logic
 */
  constructor({
    storageFactory,
    storageOptions: {
      quickFilterOptions = {},
      editableFilterOptions = {},
      savedFilterOptions = {},
      recentFilterOptions = {},
    } = {},
    entitySchema,
    odata: {
      ignoreCase = false,
      custom,
    },
    schemaProcessor = schema => schema,
    odataOptionsProcessor = odataOptions => odataOptions,
    waterfallFilterStores,
    suppressWaterfallEvents = false,
  } = {}) {
    this.entitySchema = schemaProcessor(flattenEntitySchema(entitySchema));
    this.lazyFilterSchema = _.memoize(() => new FilterSchema(this.entitySchema));
    this.odataOptions = odataOptionsProcessor({
      fieldMap: buildNameToPropertyMapping(this.entitySchema),
      ignoreCase,
      custom,
    }, this.entitySchema);
    this.filterEntityOptions = {
      entitySchema: this.entitySchema,
      odata: this.odataOptions,
    };
    const basicStorageOptions = { filterEntityOptions: this.filterEntityOptions };
    this.quickFilterStorage = storageFactory.create(
      'quickFilter',
      _.extend(quickFilterOptions, basicStorageOptions)
    );
    this.editableFilterStorage = storageFactory.create(
      'editableFilter',
      _.extend(editableFilterOptions, basicStorageOptions)
    );
    this.savedFilterStorage = storageFactory.create(
      'savedFilter',
      _.extend(savedFilterOptions, basicStorageOptions)
    );
    this.savedQuickFilterStorage = storageFactory.create(
      'savedFilter',
      _.extend(savedFilterOptions, basicStorageOptions, {
        featureName: `${storageFactory.featureName}_QuickFilter`,
      })
    );
    this.isRecentFilterSupported = !!storageFactory.preferenceService;
    if (this.isRecentFilterSupported) {
      this.recentFilterStorage = storageFactory.create(
        'recentFilter',
        _.extend(recentFilterOptions, basicStorageOptions)
      );
      this.recentQuickFilterStorage = storageFactory.create(
        'recentFilter',
        _.extend(recentFilterOptions, basicStorageOptions, {
          featureName: `${storageFactory.featureName}_QuickFilter`,
        })
      );
    }

    // internal properties
    this.editableFilter = null;
    this.quickFilter = null;
    this.dataSourceFilter = null;
    this.savedFilters = [];
    this.savedQuickFilters = [];
    this.recentFilters = [];
    this.recentQuickFilters = [];
    this.suppressWaterfallEvents = suppressWaterfallEvents;

    // Kick off first request to server to get all saved filters
    this.fetchSavedFilterPromise = this.refreshSavedFiltersCache();

    if (this.isRecentFilterSupported) {
    // Kick off first request to server to get all recent filters
      this.fetchRecentFilterPromise = this.refreshRecentFiltersCache();
    }

    // Load the saved quick filter
    this.fetchQuickFilterPromise = this.fetchQuickFilter();

    // Load the initial editable filter from storage
    this.fetchEditableFilterPromise = this.fetchEditableFilter();

    if (waterfallFilterStores) {
      this.waterfallFilterStores = waterfallFilterStores;
      this.initializeWaterfallFilters();
    }

    this.initializeCombinedFilterPromise = this.initializeCombinedFilter();

    _.extend(this, Backbone.Events);
  }

  get filterSchema() { return this.lazyFilterSchema(); }

  fetchQuickFilter() {
    return this.fetchQuickFilterForGivenStore(this.quickFilterStorage);
  }

  fetchWaterfallFilter(store) {
    return this.fetchQuickFilterForGivenStore(store)
      .then((waterfallFilter) => {
        this.waterfallFilters.push(waterfallFilter);

        this.hasActiveWaterfallFilter = this.hasActiveWaterfallFilter
          || !_.isEmpty(waterfallFilter.value.query);

        return this.waterfallFilters;
      });
  }

  fetchQuickFilterForGivenStore(quickFilterStorage) {
    return quickFilterStorage.get()
      .then((quickFilter) => {
        const filter = (quickFilter && !_.isEmpty(quickFilter.filter) && quickFilter.filter) || {
          key: '',
          value: {
            query: {},
          },
        };

        return {
          key: filter.key,
          value: filter.value,
        };
      });
  }

  fetchEditableFilter() {
    return this.editableFilterStorage.get()
      .then((editableFilter) => {
        if (editableFilter && !_.isEmpty(editableFilter.filter)) {
          const { filter } = editableFilter;

          return filter;
        }

        return editableFilter;
      }, () =>
        // Failed to load from storage, resolve back as empty initial filter.
        ({}));
  }

  // exported property and APIs, all the async calls will return promise and they will always be
  // resolved and never rejected. Resolve empty object back means the call failed.
  get p$Initialized() {
    // When both editable filter and quick filter are loaded from session storage, we treat the
    // initialization is done. Saved filter array could still being loaded.
    return this.initializeCombinedFilterPromise;
  }

  // Refreshes the promises for editable and quick filters. This is only necessary
  // with grids that don't use the filter agent to update the filters.
  refreshSource() {
    this.fetchQuickFilterPromise = this.fetchQuickFilter();
    this.fetchEditableFilterPromise = this.fetchEditableFilter();

    if (this.waterfallFilterStores) {
      this.initializeWaterfallFilters();
    }

    this.initializeCombinedFilterPromise = this.initializeCombinedFilter();
  }

  refreshWaterfallFilters() {
    if (this.waterfallFilterStores) {
      this.initializeWaterfallFilters();
      Promise.all(this.fetchWaterfallFilterPromises).then(() => {
        this.triggerQuickFilterSetEvent();
      });
    }
  }

  initializeCombinedFilter() {
    const filterPromises = {
      // Note: these information will be used by filterAgent to update UI
      editableFilter: this.fetchEditableFilterPromise,
      quickFilter: this.fetchQuickFilterPromise,
    };

    if (!_.isEmpty(this.fetchWaterfallFilterPromises)) {
      filterPromises.WaterfallFilters = Promise.all(this.fetchWaterfallFilterPromises);
    }

    return Promise.props(filterPromises).then((filters) => {
      const { editableFilter, quickFilter } = filters;

      if (editableFilter && editableFilter.query) {
        this.setInitialEditableFilter(editableFilter.query, editableFilter.name);
      } else {
        this.editableFilter = null;
      }

      if (quickFilter && quickFilter.value) {
        this.setInitialQuickFilter(quickFilter.key, quickFilter.value);
      } else {
        this.quickFilter = null;
      }

      if (this.hasActiveWaterfallFilter) {
        if (!this.suppressWaterfallEvents
          || !this.oldWaterfallFilters
          || !_.isEqual(this.oldWaterfallFilters, this.waterfallFilters)) {
          this.triggerQuickFilterSetEvent();
        }
      }

      return filters;
    });
  }

  initializeWaterfallFilters() {
    this.fetchWaterfallFilterPromises = [];
    this.oldWaterfallFilters = this.waterfallFilters;
    this.waterfallFilters = [];
    this.hasActiveWaterfallFilter = false;

    _.each(this.waterfallFilterStores, (store) => {
      this.fetchWaterfallFilterPromises.push(this.fetchWaterfallFilter(store));
    });
  }

  serialize() {
    const object = {
      version: 1,
    };
    if (this.quickFilter) {
      object.quick = this.quickFilter;
    }
    if (this.editableFilter) {
      object.editable = {
        key: this.editableFilter.name,
        value: {
          query: this.editableFilter.query,
        },
      };
    }
    if (this.hasActiveWaterfallFilter) {
      object.waterfalls = this.waterfallFilters;
    }

    return stringifySync(object);
  }

  deserialize(serialized, isColumnConversionEnabled = false) {
    const object = parseSync(serialized);

    if (object && object.version === 1) {
      // object.version now is always 1
      const {
        quick,
        editable,
        waterfalls,
      } = object;
      if (quick) {
        this.setQuickFilter(quick.key, quick.value);
      } else {
        this.removeQuickFilter();
      }
      if (editable) {
        const patchedQuery = isColumnConversionEnabled ?
          this.patchFilter(editable.value.query) : editable.value.query;
        this.setEditableFilter(patchedQuery, editable.key);
      } else {
        this.removeEditableFilter();
      }
      if (waterfalls) {
        // just ignore for now
      }
    } else {
      // object as query
      const query = object;
      if (query) {
        const quickFilter = this.getQuickFilter();
        if (quickFilter) {
          const equalQuickFilter = q => _.isEqual(q, quickFilter.value.query);
          if (equalQuickFilter(query)) {
            return;
          }
          if (
            query &&
            query.$and &&
            _.some(query.$and, equalQuickFilter)
          ) {
            const editableFilter = _.defaults({
              $and: _.reject(query.$and, equalQuickFilter),
            }, query);
            const patchedQuery = isColumnConversionEnabled ?
              this.patchFilter(editableFilter) : editableFilter;
            this.setEditableFilter(patchedQuery);
          } else {
            this.setQuickFilter(null);
            const patchedQuery = isColumnConversionEnabled ?
              this.patchFilter(query) : query;
            this.setEditableFilter(patchedQuery);
          }
        } else {
          const patchedQuery = isColumnConversionEnabled ?
            this.patchFilter(query) : query;
          this.setEditableFilter(patchedQuery);
        }
      } else {
        this.removeEditableFilter();
      }
    }
  }

  patchFilter(query) {
    let logicalOperator;
    let expressions;

    if (query.$or) {
      logicalOperator = '$or';
      expressions = query.$or;
    } else if (query.$and) {
      logicalOperator = '$and';
      expressions = query.$and;
    } else {
      logicalOperator = '$and';
      expressions = [query];
    }

    const patchedQuery = _.map(
      expressions,
      this.filterSchema.applyConversions.bind(this.filterSchema)
    );

    if (patchedQuery.length === 1) {
      return patchedQuery[0];
    }
    return _.object(
      [logicalOperator || '$and'],
      [patchedQuery]
    );
  }

  // Combined filter, used by caller for rebind grid.
  getCombinedFilter() {
    let filtersToMerge = [];
    if (this.quickFilter) {
      filtersToMerge.push(this.createFilterEntity(this.quickFilter.value.query));
    }
    if (this.editableFilter) {
      filtersToMerge.push(this.editableFilter);
    }
    if (this.dataSourceFilter) {
      filtersToMerge.push(this.createFilterEntity(this.dataSourceFilter.value.query));
    }
    if (this.hasActiveWaterfallFilter) {
      const waterfallFilterEntities = _.map(
        this.waterfallFilters,
        filter => this.createFilterEntity(filter.value.query)
      );
      filtersToMerge = filtersToMerge.concat(waterfallFilterEntities);
    }

    return mergeFilterEntity(filtersToMerge, this.filterEntityOptions);
  }

  // =============
  // [QuickFilter]
  // =============
  //
  // Save the quick filter item to storage
  setQuickFilter(key, value, isInitialLoad = false) {
    const newQuickFilter = {
      key,
      value,
    };

    if (!_.isEqual(newQuickFilter, this.quickFilter)) {
      this.quickFilter = {
        key,
        value,
      };
      if (!isInitialLoad) {
        this.quickFilterStorage.save(key, value);
      }
      this.triggerQuickFilterSetEvent();
    }
  }

  removeQuickFilter() {
    this.setQuickFilter('', { query: {} });
  }

  setInitialQuickFilter(key, value) {
    this.setQuickFilter(key, value, true);
  }

  triggerQuickFilterSetEvent() {
    this.trigger('filter-state-changed', {
      filter: this.getCombinedFilter(),
      quickFilter: this.quickFilter && this.createFilterEntity(this.quickFilter.value.query),
    }, 'quick-filter-applied');
  }

  // Get the quick filter item from storage
  getQuickFilter() {
    return this.quickFilter;
  }

  // =============
  // [DataSource Filter]
  //
  // Provides a way to set a filter only on the request
  // Note: This doesn't show on the UI or persist in storage
  // =============

  // Set the data source filter item
  setDataSourceFilter(key, value) {
    this.dataSourceFilter = {
      key,
      value,
    };
  }

  // Get the data source filter item
  getDataSourceFilter() {
    return this.dataSourceFilter;
  }

  // =============
  // [SavedFilter]
  // =============
  //
  // Get all saved filter
  getSavedFilters(refresh = false) {
    if (refresh) {
      this.fetchSavedFilterPromise = this.refreshSavedFiltersCache();
    }

    // fetchSavedFilterPromise is never rejected (see refreshSavedFiltersCache())
    return this.fetchSavedFilterPromise.then((state) => {
      if (refresh && state && state.error) {
        return state;
      }
      // Note: don't resolve the error state back if this is not a refresh request.
      //       Failed to get fetch filters from server treated as Fail-Safe.
      return _.union(this.savedFilters, this.savedQuickFilters);
    });
  }


  // =============
  // [RecentFilter]
  // =============
  //
  // Get all recent filter
  getRecentFilters(refresh = false) {
    if (refresh) {
      this.fetchRecentFilterPromise = this.refreshRecentFiltersCache();
    }

    // fetchRecentFilterPromise is never rejected (see refreshRecentFiltersCache())
    return this.fetchRecentFilterPromise.then((state) => {
      if (refresh && state && state.error) {
        return state;
      }
      // Note: don't resolve the error state back if this is not a refresh request.
      //       Failed to get fetch filters from server treated as Fail-Safe.
      return _.chain(this.recentFilters)
        .map((recentFilter, index) => {
          const quickFilter = this.recentQuickFilters[index];
          if (!_.isEmpty(recentFilter.query) || !_.isEmpty(quickFilter.query)) {
            return {
              editableFilter: recentFilter,
              quickFilter: this.recentQuickFilters[index],
            };
          }
          return null;
        })
        .compact()
        .value();
    });
  }

  // =============
  // [RecentFilter]
  // =============
  //
  // Get recent filter by index
  getRecentFiltersByIndex(index) {
    return [
      this.recentQuickFilters[index],
      this.recentFilters[index],
    ];
  }

  // Save the filter to server, resolve back the recent filter (type of FilterEntity)
  addUpdateRecentFilter(
    query,
    filterStorageType = FilterStorageType.EditableFilter
  ) {
    const recentFilterStorage = filterStorageType === FilterStorageType.EditableFilter ?
      this.recentFilterStorage : this.recentQuickFilterStorage;
    const filter = query ? this.createFilterEntity(query) : null;

    return recentFilterStorage.save(filter)
      .then(() => {
        this.refreshRecentFiltersCache();
      }, (errorData) => {
        this.trigger('filter-operation-failed', errorData);
        return errorData;
      });
  }

  // Save the filter to server, resolve back the saved filter (type of FilterEntity)
  addUpdateSavedFilter(
    query,
    name,
    filterStorageType = FilterStorageType.EditableFilter, updateAppliedFilter = true
  ) {
    if (!query || !name) {
      throw new Error(`Query ${query} or name ${name} is missing`);
    }
    return this.getFilterByName(name, filterStorageType).then((existingFilter) => {
      const filter = this.createFilterEntity(query, name);
      if (existingFilter) {
        _.extend(filter, {
          id: existingFilter.id,
        });
      }

      if (updateAppliedFilter) {
        if (filterStorageType === FilterStorageType.EditableFilter) {
          this.setEditableFilter(filter);
        } else {
          this.setQuickFilter('', {
            query,
          });
        }
      }

      const savedFilterStorage = filterStorageType === FilterStorageType.EditableFilter ?
        this.savedFilterStorage : this.savedQuickFilterStorage;
      const savedFilters = filterStorageType === FilterStorageType.EditableFilter ?
        this.savedFilters : this.savedQuickFilters;

      return savedFilterStorage.save(filter)
        .then((savedData) => {
          // successfully saved to filter, now update the saved list
          _.extend(filter, { id: savedData.id });

          const index = _.findIndex(
            savedFilters,
            savedFilter => savedFilter.id === savedData.id
          );

          if (index === -1) {
            savedFilters.push(filter);
          } else {
            savedFilters.splice(index, 1, filter);
          }

          // The caller should not need to wait for refetch, resolve the saved filter back
          return filter;
        }, (errorData) => {
          this.trigger('filter-operation-failed', errorData);
          return errorData;
        });
    });
  }

  // Delete filter from storage, resolve the deleted filter's name
  deleteSavedFilter(filter, filterStorageType = FilterStorageType.EditableFilter) {
    const savedFilterStorage = filterStorageType === FilterStorageType.EditableFilter ?
      this.savedFilterStorage : this.savedQuickFilterStorage;
    const savedFilters = filterStorageType === FilterStorageType.EditableFilter ?
      this.savedFilters : this.savedQuickFilters;

    // return this.deleteFromServer(data)
    return savedFilterStorage.delete(filter)
      .then((deletedResult) => {
        // Deleted from server, update local cache.
        const index = _.findIndex(savedFilters, { id: deletedResult.id });

        if (index !== -1) {
          savedFilters.splice(index, 1);
        }
        return { name: filter.name };
      }, (errorData) => {
        this.trigger('filter-operation-failed', errorData);
        return errorData;
      });
  }

  refreshSavedOrRecentFiltersCache(
    editableFilterStorage,
    quickFilterStorage,
    editableFilters,
    quickFilters
  ) {
    return Promise.all([
      editableFilterStorage.get(),
      quickFilterStorage.get()])
      .then((combinedFilters) => {
        // Re-populate the cache
        editableFilters.length = 0;
        _.each(combinedFilters[0], (filter) => {
          editableFilters.push(this.createFilterEntity(
            filter.query,
            filter.name,
            filter.id
          ));
        });
        quickFilters.length = 0;
        _.each(combinedFilters[1], (filter) => {
          quickFilters.push(this.createFilterEntity(
            filter.query,
            filter.name,
            filter.id
          ));
        });
      }, errorData =>
        // Don't touch the cached savedFilters, still resolve the fetchSavedFilterPromise
        // with error object
        errorData);
  }

  // Internal function
  refreshSavedFiltersCache() {
    return this.refreshSavedOrRecentFiltersCache(
      this.savedFilterStorage,
      this.savedQuickFilterStorage,
      this.savedFilters,
      this.savedQuickFilters
    );
  }

  // Internal function
  refreshRecentFiltersCache() {
    return this.refreshSavedOrRecentFiltersCache(
      this.recentFilterStorage,
      this.recentQuickFilterStorage,
      this.recentFilters,
      this.recentQuickFilters
    );
  }

  getFilterByName(name, filterStorageType = FilterStorageType.EditableFilter) {
    // Note: fetchSavedFilterPromise is never rejected
    return this.fetchSavedFilterPromise.then(() =>
      // Return the filter entity, if the last fetch failed, use the local savedFilters directly.
      _.find(filterStorageType === FilterStorageType.EditableFilter ?
        this.savedFilters : this.savedQuickFilters, { name }));
  }

  // ===============
  // [EditableFilter]
  // ===============
  //
  // Get Filter for current editable filter, synchronous call
  getEditableFilter() {
    return this.editableFilter;
  }

  // Remove the current editable filter
  removeEditableFilter() {
    this.editableFilter = null;

    this.editableFilterStorage.clean();

    this.trigger('filter-state-changed', {
      filter: this.getCombinedFilter(),
    }, 'editable-filter-removed');
  }

  // Set the current editable filter
  setEditableFilterByName(name) {
    return this.getFilterByName(name).then((filter) => {
      if (filter) {
        this.setEditableFilter(filter, name);
      } else {
        throw new Error(`Cannot find filter by name ${name}`);
      }
    });
  }

  // Set the current editable filter
  setEditableFilter(filter, name, isInitialLoad = false, callback = null) {
    const filterEntity = filter instanceof FilterEntity ?
      filter : this.createFilterEntity(filter, name);

    const validatedFilter = getValidFilterEntity(filterEntity);

    if ((!_.isEmpty(filter.query) && !validatedFilter)
      || (validatedFilter && filterEntity && validatedFilter.depth !== filterEntity.depth)) {
      this.trigger('invalid-filter-property', {
        originalFilter: filterEntity.query,
        validatedFilter: validatedFilter ? validatedFilter.query : null,
      });
    }

    // Only trigger filter state change if filter changed
    if ((!this.editableFilter && validatedFilter) ||
      (this.editableFilter && !validatedFilter) ||
      (this.editableFilter && validatedFilter &&
        !_.isEqual(this.editableFilter.query, validatedFilter.query))) {
      this.editableFilter = validatedFilter;
      this.trigger('filter-state-changed', {
        filter: this.getCombinedFilter(),
      }, 'editable-filter-applied');
      if (!isInitialLoad) {
        this.editableFilterStorage.save(this.editableFilter).then(() => {
          if (_.isFunction(callback)) {
            callback();
          }
        });
      }
    } else {
      this.trigger('editable-filter-applied');
      if (_.isFunction(callback)) {
        callback();
      }
    }
  }

  setInitialEditableFilter(filter, name) {
    this.setEditableFilter(filter, name, true);
  }

  createFilterEntity(query, name, id) {
    return new FilterEntity(
      query,
      this.filterEntityOptions,
      name,
      id
    );
  }

  // Remove the current applied filter
  removeAppliedFilter() {
    this.quickFilter = {
      key: '',
      value: {
        query: {},
      },
    };
    this.editableFilter = null;

    this.editableFilterStorage.clean();
    this.quickFilterStorage.clean();

    this.trigger('filter-state-changed', {
      filter: this.getCombinedFilter(),
    }, 'filter-reset');
  }
}
